import React from "react";
import { resolveAsset } from "../lib/api";
import { parseMedia } from "../lib/media";
import { Facebook, Youtube } from "lucide-react";

/**
 * Renders a gallery item that may be: an image URL, a YouTube link, or a Facebook reel/video/post URL.
 * `cacheBust` — optional stable token (typically the item's created_at) appended as ?v= to prevent
 * browsers from showing an old cached image after an admin edit.
 */
const cacheBustSrc = (src, cacheBust) => {
  if (!src || !cacheBust) return src;
  const token = String(cacheBust).replace(/[^0-9A-Za-z]/g, "").slice(-10);
  if (!token) return src;
  return src + (src.includes("?") ? "&" : "?") + "v=" + token;
};

const MediaItem = ({ url, caption, aspectClassName = "aspect-square", className = "", testId, eager = false, cacheBust }) => {
  const m = parseMedia(url);

  if (m.type === "image") {
    const src = cacheBustSrc(resolveAsset(m.src), cacheBust);
    return (
      <figure className={`relative overflow-hidden ${aspectClassName} ${className}`} data-testid={testId}>
        <img
          src={src}
          alt={caption || "Gallery"}
          loading={eager ? "eager" : "lazy"}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
        />
        {caption && (
          <figcaption className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/60 to-transparent text-white text-sm font-medium">
            {caption}
          </figcaption>
        )}
      </figure>
    );
  }

  const videoAspect =
    aspectClassName === "aspect-square"
      ? (m.aspect === "9/16" ? "aspect-[9/16]" : "aspect-video")
      : aspectClassName;

  const Icon = m.type === "youtube" ? Youtube : Facebook;
  const badgeColor = m.type === "youtube" ? "bg-red-600 text-white" : "bg-[#1877F2] text-white";

  return (
    <figure className={`relative overflow-hidden ${videoAspect} ${className} bg-black/5`} data-testid={testId}>
      <iframe
        src={m.embedUrl}
        title={caption || m.label}
        loading="lazy"
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share; fullscreen"
        allowFullScreen
        className="w-full h-full border-0"
        scrolling="no"
        frameBorder="0"
      />
      <div className={`absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full text-[0.65rem] font-bold ${badgeColor} shadow-md pointer-events-none`}>
        <Icon size={12} /> {m.label}
      </div>
      {caption && (
        <figcaption className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/70 to-transparent text-white text-sm font-medium pointer-events-none">
          {caption}
        </figcaption>
      )}
    </figure>
  );
};

export default MediaItem;
